import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Loader2, ChevronLeft, ClipboardList, RotateCcw } from 'lucide-react';
import { adminGetSettings, adminUpdateSettings, adminResetDemoAccount } from '../services/api';

const ADMIN_KEY_STORAGE = 'orosz_admin_key_v1';

const AdminSettingsPage = () => {
  const navigate = useNavigate();
  const adminKey = localStorage.getItem(ADMIN_KEY_STORAGE) || '';
  const [settings, setSettings] = useState(null);
  const [busy, setBusy] = useState(false);
  const [demoBusy, setDemoBusy] = useState(false);
  const [demoStats, setDemoStats] = useState(null);

  useEffect(() => {
    if (!adminKey) { navigate('/admin'); return; }
    adminGetSettings(adminKey)
      .then((d) => setSettings(d.settings))
      .catch(() => toast.error('A beállítások betöltése nem sikerült.'));
  }, [adminKey, navigate]);

  const toggle = async (next) => {
    setBusy(true);
    try {
      const d = await adminUpdateSettings(adminKey, { paid_assessment: next });
      setSettings(d.settings);
      toast.success(next
        ? 'Fizetős állapotfelmérés BEKAPCSOLVA (10.000 Ft megjelenik a honlapon).'
        : 'Ingyenes állapotfelmérés — a 10.000 Ft-os díj mindenhonnan eltűnt.');
    } catch {
      toast.error('A mentés nem sikerült.');
    } finally {
      setBusy(false);
    }
  };

  const paid = !!settings?.paid_assessment;

  const resetDemo = async () => {
    if (!window.confirm(
      'Biztosan visszaállítod a demo@oroszcoaching.hu fiókot?\n\n'
      + 'A fiók minden naplója törlődik, és újraíródik az 1 hónapos bemutató-adat '
      + '(edzésterv, edzésnaplók, mérések, étrend, étkezési napló, bérletek).')) return;
    setDemoBusy(true);
    try {
      const d = await adminResetDemoAccount(adminKey);
      setDemoStats(d.stats);
      toast.success('A demó fiók visszaállt — mehet a bemutató.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'A visszaállítás nem sikerült.');
    } finally {
      setDemoBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-200">
        <div className="container mx-auto px-6 py-4 flex items-center gap-3">
          <button
            data-testid="settings-back"
            onClick={() => navigate('/admin')}
            className="text-gray-500 hover:text-gray-900"
          >
            <ChevronLeft size={20} />
          </button>
          <h1 className="text-lg font-bold text-gray-900">Beállítások</h1>
        </div>
      </header>

      <main className="container mx-auto px-6 py-8 max-w-2xl">
        {!settings ? (
          <div className="flex items-center gap-2 text-gray-500"><Loader2 className="animate-spin" size={18} /> Betöltés…</div>
        ) : (
          <div
            data-testid="setting-paid-assessment"
            className="bg-white border border-gray-200 rounded-xl p-6"
          >
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-yellow-100 grid place-items-center shrink-0">
                <ClipboardList size={18} className="text-yellow-700" />
              </div>
              <div className="min-w-0">
                <h2 className="text-base font-bold text-gray-900">Fizetős állapotfelmérés (10.000 Ft)</h2>
                <p className="text-sm text-gray-600 mt-1 leading-relaxed">
                  {paid
                    ? 'Jelenleg a honlapon a 10.000 Ft-os felmérési díj látszik (programkártyák, GYIK, ÁSZF, foglalási ablak, landing oldalak), és a megvásárolható „Online Konzultáció" csomag is elérhető.'
                    : 'Az állapotfelmérés jelenleg ingyenes: a 10.000 Ft-os díj sehol nem jelenik meg (programkártyák, GYIK, ÁSZF, foglalási ablak, landing oldalak), és a megvásárolható „Online Konzultáció" csomag is rejtve van.'}
                </p>
              </div>
            </div>

            <div className="mt-5 flex items-center justify-between gap-4 rounded-lg border border-gray-200 p-4">
              <div>
                <div className="text-sm font-bold text-gray-900">
                  Jelenlegi állapot:{' '}
                  <span className={paid ? 'text-amber-700' : 'text-emerald-700'}>
                    {paid ? 'Fizetős (10.000 Ft)' : 'Ingyenes'}
                  </span>
                </div>
                <div className="text-xs text-gray-500 mt-0.5">
                  A változás a látogatóknál az oldal újratöltése után látszik.
                </div>
              </div>
              <Button
                data-testid="toggle-paid-assessment"
                disabled={busy}
                onClick={() => toggle(!paid)}
                className={paid
                  ? 'bg-emerald-600 hover:bg-emerald-700 text-white font-bold'
                  : 'bg-gray-900 hover:bg-gray-800 text-white font-bold'}
              >
                {busy ? <Loader2 className="animate-spin" size={16} />
                  : paid ? 'Legyen ingyenes' : 'Legyen fizetős (10.000 Ft)'}
              </Button>
            </div>
          </div>
        )}

        <div data-testid="setting-demo-reset" className="bg-white border border-gray-200 rounded-xl p-6 mt-6">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-100 grid place-items-center shrink-0">
              <RotateCcw size={18} className="text-blue-700" />
            </div>
            <div className="min-w-0">
              <h2 className="text-base font-bold text-gray-900">Demó fiók visszaállítása</h2>
              <p className="text-sm text-gray-600 mt-1 leading-relaxed">
                Egy kattintással újratölti a <span className="font-semibold">demo@oroszcoaching.hu</span> fiókot
                úgy, mintha az ügyfél egy hónapja aktívan használná: aktív edzésterv, 5 hétnyi edzésnapló,
                heti mérések kaliperrel, kiadott étrend, 30 napnyi étkezési napló, kalkulátor-mentések,
                bérletek és foglalások. A fiók korábbi demó adatai törlődnek.
              </p>
            </div>
          </div>

          <div className="mt-5 flex items-center justify-between gap-4 rounded-lg border border-gray-200 p-4">
            <div className="text-xs text-gray-500">
              {demoStats
                ? `Kész: ${demoStats.workout_logs} edzésnapló · ${demoStats.food_log_entries} étkezés · ${demoStats.measurements} mérés · ${demoStats.passes} bérlet`
                : 'Bemutató előtt érdemes lefuttatni, hogy friss dátumokkal lássák az adatokat.'}
            </div>
            <Button
              data-testid="reset-demo-account"
              disabled={demoBusy}
              onClick={resetDemo}
              className="bg-blue-600 hover:bg-blue-700 text-white font-bold shrink-0"
            >
              {demoBusy ? <Loader2 className="animate-spin" size={16} /> : 'Demó feltöltése'}
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminSettingsPage;
